import lodash from 'lodash';
import { getFieldsFromArray } from '../utils/getFieldsFromArray';
import { getHighestValueFromArray } from '../utils/getHighestValueFromArray';
import {
  countryCodeInArrays,
  countryNamesInArrays,
  nationalitiesInArrays,
  activityCodePath,
  customerTypePath,
} from '../constants/cra.dnb.constants';
import { ICraInternalObject } from '../types/ICra';
import {
  CraPreReqMapToCraDefaults,
  RiskScoreForCountriesFiltering,
  PrimaryCountryFieldForCountryDetailsJSON,
  CraToRiskFactorDescription,
} from 'src/constants/cra.constants';
// Todo: This to be removed once craDefaults has country mapping done
import { countriesDetails } from 'src/constants/countriesDetails';

/** --- --- DnB CRA Getting Fields for Pre Req Object --- --- **/

/**
 * @summary This gets the country codes that resides in array of objects inside company profile object
 * The structure of countryCodeArray is { key: string, arrayPath: string, fieldPath: string }
 * @param companyProfile
 * @returns countryCodes object = {
 *  string: string[]
 * }
 * e.g.
 * {
 *  'directorsAndShareholders.directors.residenceCountry' : ['IT', 'IT'],
 *   'directorsAndShareholders.shareholders.residenceCountry': ['IT','IT', 'IT']
 * } */
export const getCountryCodes = (companyProfile: any) => {
  let countryCodes = {};
  // get country codes from arrays
  for (let element of countryCodeInArrays) {
    const { key, arrayPath, fieldPath } = element;
    const fields = getFieldsFromArray(companyProfile, arrayPath, fieldPath);
    if (fields && fields.length) {
      countryCodes = {
        ...countryCodes,
        [`${arrayPath}.${fieldPath}`]: fields,
      };
    }
  }
  // console.log(countryCodes);
  return countryCodes;
};

/**
 * @summary This gets the country names that resides in array of objects inside company profile object
 * The structure of countryNamesInArrays is { key: string, arrayPath: string, fieldPath: string }
 * @param companyProfile
 * @returns countryNames object = {
 *  string: string[]
 * }
 * e.g.
 * {
 *  'directorsAndShareholders.directors.residenceCountry' : ['italia'],
 *   'beneficialOwners.residenceCountry': ['Italy','italy']
 * }
 */
export const getCountryNames = (companyProfile: any) => {
  let countryNames = {};
  // get country codes from arrays
  for (let element of countryNamesInArrays) {
    const { key, arrayPath, fieldPath } = element;
    const fields = getFieldsFromArray(companyProfile, arrayPath, fieldPath);
    if (fields && fields.length) {
      countryNames = {
        ...countryNames,
        [`${arrayPath}.${fieldPath}`]: fields,
      };
    }
  }
  // console.log(countryCodes);
  return countryNames;
};

/**
 * @summary This gets the nationalities that resides in array of objects inside company profile object
 * The structure of this object is { key: string, arrayPath: string, fieldPath: string }
 * @param companyProfile
 * @returns nationalities object = {
 *  string: string[]
 * }
 * e.g.
 * {
 *  'directorsAndShareholders.directors.nationality' : ['British', 'italian'],
 *   'directorsAndShareholders.shareholders.nationality': ['English','Afghani', 'Arab']
 * } */
export const getNationalities = (companyProfile: any) => {
  // get nationalities from array
  let nationalities = {};
  for (let element of nationalitiesInArrays) {
    const { key, arrayPath, fieldPath } = element;
    const fields = getFieldsFromArray(companyProfile, arrayPath, fieldPath);
    if (fields && fields.length) {
      nationalities = {
        ...nationalities,
        [`${arrayPath}.${fieldPath}`]: fields,
      };
    }
  }
  return nationalities;
};

/**
 * This gets activity codes out of the profile object
 * @param companyProfile
 * @returns activity codes or empty array
 */
export const getActivityCodes = (companyProfile: any) => {
  const activityCodes = lodash.get(companyProfile, activityCodePath);
  return activityCodes && activityCodes.length ? activityCodes : [];
};

/**
 * This returns the customer type from the company profile object
 * The path to customer type is stored in a constants file which is being used here
 * @param companyProfile
 * @returns customer type
 */
export const getCustomerType = (companyProfile: any) => {
  try {
    return lodash.get(companyProfile, customerTypePath);
  } catch (error: any) {
    console.error(error && error?.message);
  }
};

/**
 * This method returns the highest Risk Score along with its Path and expects an Object countryRatingsMap not an array
 * Also the values inside should be an array for this function to Work
 * It expects an object which has Key: string and value: []
 * It traverses over the object and gets the highest value for each of the entry
 * In the next iteration, it compares if the new highest value is > than the previous highest value
 * If yes, it makes the switch, else it continues to the next entry

 * @param countryRatingsMap { addresses.countryCode: [ {RATING: 'MEDIUM', SCORE: 2}],  beneficialOwners.countryCode: [{RATING: 'LOW', SCORE: 1}]}
 * a more complete sample on countryRatingsMap is in packages\client\src\tests\mocks\craCountriesWithAboveLowRislFromAtlanticTopco.ts
 * @returns highestRiskRating { addresses.countryCode: {RATING: 'MEDIUM', SCORE: 2}}
 */
export const getHighestCountryRiskWithPath = (countryRatingsMap: any) => {
  try {
    if (
      !countryRatingsMap ||
      typeof countryRatingsMap !== 'object' ||
      !Object.keys(countryRatingsMap).length
    ) {
      console.warn('Missing or Invalid countryRatingsAboveMedium parameter received');
      return;
    }
    // These variables will keep check of the highest risk score and its path
    let highestRiskRating: any = {};
    // traverse over the object to find the highest risk score value
    for (let [path, values] of Object.entries(countryRatingsMap)) {
      // if it is the first traversal
      if (!Object.keys(highestRiskRating).length && values && Object.keys(values).length) {
        // get the highest value from the current entry
        const highestRiskScoreForCountry = getHighestValueFromArray(values, 'score');
        if (highestRiskScoreForCountry) {
          highestRiskRating = highestRiskScoreForCountry;
        }
      }

      // if the object exists,
      if (
        highestRiskRating &&
        Object.keys(highestRiskRating) &&
        values &&
        Object.keys(values).length
      ) {
        // get the highest value from the current entry/item
        const highestValueFromNextItem = getHighestValueFromArray(values, 'score');
        // if the new value has more SCORE value than the stored one, replace
        if (
          highestValueFromNextItem &&
          highestRiskRating?.score < highestValueFromNextItem?.score
        ) {
          highestRiskRating = highestValueFromNextItem;
        }
      }
    }
    return highestRiskRating;
  } catch (error: any) {
    console.error(error && error?.message);
  }
};

/**
 * This method is responsible for mapping countries to their correponding RiskRatings in the CRA Defaults File
 * It will pick up en entry from craPreReq.countries (ICraInternalObject) and find its matching RiskRating
 * e.g. in craPreReq object we used to have
 * "address.otherAddresses":  ["IT"...], this will become => "address.otherAddresses" : [{RATING: 'MEDIUM', SCORE: 2}...]]
 * Note that this will happen for both countries.countryCodes and countries.countryNames
 * @param craPreReq
 * @param craDefaultValues
 * @returns country Ratings { addresses.countryCode: [ {RATING: 'MEDIUM', SCORE: 2}],  beneficialOwners.countryCode: [{RATING: 'LOW', SCORE: 1}]}
 */
export const getCountriesRiskRatings = (craPreReq: ICraInternalObject, craDefaultValues: any) => {
  try {
    const countryRatings: any = {};
    // get country risk / jurisdiction
    const { countries } = craPreReq;
    let { countryCodes } = countries;
    let { countryNames } = countries;
    let { nationalities } = countries;

    if (countryCodes && typeof countryCodes === 'object') {
      // Get countries risk from the Country codes
      for (let [path, codes] of Object.entries(countryCodes)) {
        countryRatings[path] = [];
        // { path: "addresses.countryCode", codes: ["IT", "IT", "AB"] }
        for (let countryCode of codes) {
          // get iso3 for this iso2
          const filteredItem = countriesDetails.filter((item: any) => {
            return item.alpha2Code === countryCode;
          });
          const iso3Code = filteredItem && filteredItem.length && filteredItem[0]?.alpha3Code;
          // check if isoCode exist for the correponding country name. It also catches if any country was not found
          if (iso3Code) {
            // get the corresponding data for iso3 code
            const ratingScore = craDefaultValues[CraPreReqMapToCraDefaults.country][iso3Code];
            countryRatings[path].push({
              riskFactor: CraToRiskFactorDescription.jurisdiction,
              source: path,
              attribute: filteredItem[0][PrimaryCountryFieldForCountryDetailsJSON],
              iso2: countryCode,
              rating: ratingScore.RATING,
              score: ratingScore.SCORE,
            });
          } else {
            console.warn(
              `Could not find iso3Code for countryCode = ${countryCode}. Please match spellings and case`,
            );
          }
        }
      }
    }

    if (countryNames && typeof countryNames === 'object') {
      // Get countries risk from the Country names
      for (let [path, names] of Object.entries(countryNames)) {
        countryRatings[path] = [];
        // { path: addresses.countryNames, names: [italy, italy, albania] }
        for (let countryName of names) {
          // get iso3 for this iso2
          const filteredItem = countriesDetails.filter((item: any) => {
            return item.englishShortName === countryName;
          });
          const iso3Code = filteredItem && filteredItem.length && filteredItem[0]?.alpha3Code;
          if (iso3Code) {
            // get the corresponding data for iso3 code
            const ratingScore = craDefaultValues[CraPreReqMapToCraDefaults.country][iso3Code];
            countryRatings[path].push({
              riskFactor: CraToRiskFactorDescription.jurisdiction,
              source: path,
              attribute: filteredItem[0][PrimaryCountryFieldForCountryDetailsJSON],
              iso2: filteredItem[0]?.alpha2Code,
              rating: ratingScore.RATING,
              score: ratingScore.SCORE,
            });
          } else {
            console.warn(
              `Warning: Could not find iso3Code for country Name = ${countryName}. Please check spellings and case match`,
            );
          }
        }
      }
    }

    if (nationalities && typeof nationalities === 'object') {
      // Get countries risk from the Country names
      for (let [path, values] of Object.entries(nationalities)) {
        countryRatings[path] = [];
        // { path: directors.nationalities, values: ["British", "English", "Italian"] }
        for (let nationality of values) {
          // get iso3 for this iso2
          const filteredItem = countriesDetails.filter((item: any) =>
            item.nationality.split(',').includes(nationality),
          );
          const iso3Code = filteredItem && filteredItem.length && filteredItem[0]?.alpha3Code;
          if (iso3Code) {
            // get the corresponding data for iso3 code
            const ratingScore = craDefaultValues[CraPreReqMapToCraDefaults.country][iso3Code];
            countryRatings[path].push({
              riskFactor: CraToRiskFactorDescription.jurisdiction,
              source: path,
              attribute: filteredItem[0][PrimaryCountryFieldForCountryDetailsJSON],
              iso2: filteredItem[0]?.alpha2Code,
              rating: ratingScore.RATING,
              score: ratingScore.SCORE,
            });
          } else {
            console.warn(
              `Warning: Could not find iso3Code for nationality = ${nationality}. Please check spellings and case match`,
            );
          }
        }
      }
    }
    return countryRatings;
  } catch (error: any) {
    console.error(error && error?.message);
  }
};

/**
 * This method filters through the formulated country risk rating map, and returns only the entries above or Equal to Medium Risk Score
 * @param countryRatings { addresses.countryCode: [ {RATING: 'MEDIUM', SCORE: 2}],  beneficialOwners.countryCode: [{RATING: 'LOW', SCORE: 1}]}
 * @returns countryRatingsAboveMedium { addresses.countryCode: [ {RATING: 'MEDIUM', SCORE: 2}] }
 */
export const getCountriesAboveOrMediumRisk = (countryRatings: any) => {
  try {
    // filter through the existing countryRatings object, such that you collect all values above and equal to 2 Risk Score
    // address.countryCode : [ {Rating: "Medium", Score: 2}, {Rating: "Low", Score: 0}, {Rating: "Low", Score: 0}, {Rating: "High", Score: 3} ];
    let countryRatingsAboveMedium = {};
    for (let [path, values] of Object.entries(countryRatings)) {
      // filter all values above and equal to medium score
      let mediumOrAboveRiskValues: any =
        values &&
        Array.isArray(values) &&
        values.filter((item: any) => item.score >= RiskScoreForCountriesFiltering);
      // if there are values above medium risk, add them to the object
      if (mediumOrAboveRiskValues && mediumOrAboveRiskValues.length) {
        countryRatingsAboveMedium = {
          ...countryRatingsAboveMedium,
          [path]: mediumOrAboveRiskValues,
        };
      }
    }
    return countryRatingsAboveMedium;
  } catch (error: any) {
    console.error(error && error?.message);
  }
};
